import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { trpc } from '@/trpc/client';
import { unifiedDiff } from './diff';

export function CommonEdit(): JSX.Element {
  const { user } = useAuth();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.prompts.common.get.useQuery();
  const [draft, setDraft] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const update = trpc.prompts.common.update.useMutation({
    onSuccess: async () => {
      await utils.prompts.common.get.invalidate();
      setConfirming(false);
      setDraft(null);
      navigate('/prompts');
    },
  });

  const canEdit = user?.role === 'editor' || user?.role === 'admin';
  const text = draft ?? data?.text ?? '';
  const saveDisabled = !canEdit || text.length === 0 || text === data?.text;

  if (isLoading) return <p>読み込み中...</p>;

  return (
    <div className="max-w-3xl">
      <header className="mb-3 flex items-center justify-between">
        <h1 className="text-lg font-semibold">共通プロンプト</h1>
        <div className="flex gap-2">
          <button
            type="button"
            className="rounded border px-3 py-1 text-sm"
            onClick={() => navigate('/prompts/common/history')}
          >
            履歴
          </button>
          {canEdit && (
            <>
              <button
                type="button"
                className="rounded border px-3 py-1 text-sm"
                disabled={draft === null}
                onClick={() => setDraft(null)}
              >
                破棄
              </button>
              <button
                type="button"
                className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:bg-gray-300"
                disabled={saveDisabled}
                onClick={() => setConfirming(true)}
              >
                保存
              </button>
            </>
          )}
        </div>
      </header>
      <p className="mb-2 text-xs text-gray-500">
        役割・口調・末尾ニュアンスなど。すべての応答に適用されます。
      </p>
      <textarea
        className="min-h-[300px] w-full rounded border p-2 font-mono text-sm"
        readOnly={!canEdit}
        value={text}
        onChange={(e) => setDraft(e.target.value)}
      />

      {confirming && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/30">
          <div className="w-[600px] rounded bg-white p-4 shadow">
            <h2 className="mb-2 text-base font-semibold">変更を確認して保存</h2>
            <div className="mb-3 max-h-64 overflow-auto rounded border bg-gray-50 p-2 font-mono text-xs">
              {unifiedDiff(data?.text ?? '', text).map((line, idx) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: diff lines have no stable id
                  key={idx}
                  className={
                    line.kind === 'add'
                      ? 'bg-green-100'
                      : line.kind === 'remove'
                        ? 'bg-red-100'
                        : ''
                  }
                >
                  {line.kind === 'add' ? '+ ' : line.kind === 'remove' ? '- ' : '  '}
                  {line.text}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="rounded border px-3 py-1 text-sm"
                onClick={() => setConfirming(false)}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="rounded bg-blue-600 px-3 py-1 text-sm text-white"
                onClick={() => update.mutate({ text })}
                disabled={update.isPending}
              >
                保存して反映
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
